import { useEffect, useRef, useState } from "react";

export default function useLocationSearch(map) {
  const autocompleteRef = useRef(null);
  const requestRef = useRef(0);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const input = query.trim();
    if (input.length < 2 || !window.google?.maps?.places?.AutocompleteService) {
      setSuggestions([]);
      return undefined;
    }

    autocompleteRef.current ||= new window.google.maps.places.AutocompleteService();
    const requestId = ++requestRef.current;
    const timer = setTimeout(() => {
      autocompleteRef.current.getPlacePredictions({ input }, (predictions, status) => {
        if (requestId !== requestRef.current) return;
        const ok = status === window.google.maps.places.PlacesServiceStatus.OK;
        setSuggestions(ok ? (predictions || []).slice(0, 5) : []);
      });
    }, 220);

    return () => clearTimeout(timer);
  }, [query]);

  const search = (searchQuery = query) => {
    const value = searchQuery.trim();
    if (!value || !map || !window.google?.maps?.Geocoder) return;
    setIsSearching(true);
    setError("");
    new window.google.maps.Geocoder().geocode({ address: value }, (results, status) => {
      setIsSearching(false);
      if (status !== "OK" || !results?.[0]) {
        setError("Location not found. Try a more specific search.");
        return;
      }
      const result = results[0];
      if (result.geometry.viewport) map.fitBounds(result.geometry.viewport);
      else {
        map.panTo(result.geometry.location);
        map.setZoom(15);
      }
      setQuery(result.formatted_address || value);
      setSuggestions([]);
    });
  };

  const selectSuggestion = (suggestion) => {
    setQuery(suggestion.description);
    setSuggestions([]);
    search(suggestion.description);
  };

  return { query, setQuery, suggestions, isSearching, error, search, selectSuggestion };
}

